import {
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { setAuthToken, setRefreshToken } from 'helpers/token';
import { IRestApiResponse } from 'interfaces';
import { coreSliceActions } from 'base/core/slice';
import { capitalize } from 'lodash';
import { authSliceActions } from 'base/auth/slice';
import API from 'base/auth/constants';
import { IMfaVerificationForm, IMfaChallengeAction } from './interface';
import { mfaSliceActions } from './slice';
import { selectMfaState } from './selectors';

export function* initiateMfaChallengeGenerator({ payload }: IMfaChallengeAction): any {
  try {
    const mfaState = yield select(selectMfaState);
    const { originalCredentials, challengeId } = mfaState;

    // * If challenge is already initiated, don't make another request
    if (challengeId) {
      yield put(mfaSliceActions.initiateMfaChallengeSucceeded({ challengeId }));
      return;
    }

    if (!originalCredentials) {
      const errorMessage = 'Original credentials not found';
      yield put(
        coreSliceActions.setMessage({
          message: errorMessage,
          success: false,
        }),
      );
      yield put(mfaSliceActions.initiateMfaChallengeFailed(errorMessage));
      return;
    }

    const challengeData = {
      name: originalCredentials.name,
      password: originalCredentials.password,
      mfaType: payload.mfaType,
      isDispalyMfaChallenge: true,
    };

    const response: IRestApiResponse = yield call(request, API.POST_SIGN_IN, challengeData, false);

    if (response.statusCode === 200 && response.data?.challengeId) {
      yield put(
        mfaSliceActions.initiateMfaChallengeSucceeded({
          challengeId: response.data.challengeId,
        }),
      );
    } else if (response.statusCode === 202) {
      yield put(
        coreSliceActions.setMessage({
          message: 'Instructions to set up MFA have been sent to your email.',
          success: true,
        }),
      );
      yield put(authSliceActions.clearLoadingForMfa());
    } else if (response.statusCode === 303) {
      // * Backend returned 303 again - this means the challenge was initiated successfully
      // * and the challengeId should be in the response data
      if (response.data?.challengeId) {
        yield put(
          mfaSliceActions.initiateMfaChallengeSucceeded({
            challengeId: response.data.challengeId,
          }),
        );
      } else {
        // * 303 without challengeId - this is an error
        const errorMessage = 'MFA challenge initiation failed - no challenge ID received';
        yield put(
          coreSliceActions.setMessage({
            message: errorMessage,
            success: false,
          }),
        );
        yield put(mfaSliceActions.initiateMfaChallengeFailed(errorMessage));
      }
    } else {
      const errorMessage = response?.message
        ? capitalize(response?.message)
        : 'Failed to initiate MFA challenge';
      yield put(
        coreSliceActions.setMessage({
          message: errorMessage,
          success: false,
        }),
      );
      yield put(mfaSliceActions.initiateMfaChallengeFailed(errorMessage));
    }
  } catch (error: any) {
    let errorMessage = 'Failed to initiate MFA challenge';
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    yield put(
      coreSliceActions.setMessage({
        message: errorMessage,
        success: false,
      }),
    );
    yield put(mfaSliceActions.initiateMfaChallengeFailed(errorMessage));
    yield put(mfaSliceActions.resetMfaState());
  }
}

export function* verifyMfaGenerator({
  payload,
}: {
  payload: IMfaVerificationForm;
  type: string;
}): any {
  const defaultErrorMessage = 'Invalid MFA code or code has expired. Please check your code and try again.';
  try {
    const mfaState: any = yield select(selectMfaState);
    const { originalCredentials, challengeId } = mfaState;

    if (!originalCredentials) {
      const errorMessage = 'Original credentials not found. Please restart the sign-in process.';
      yield put(
        coreSliceActions.setMessage({
          message: errorMessage,
          success: false,
        }),
      );
      yield put(mfaSliceActions.verifyMfaFailed(errorMessage));
      yield put(mfaSliceActions.resetMfaState());
      return;
    }

    if (!challengeId) {
      const errorMessage = 'Challenge ID not found. Please restart the MFA process.';
      yield put(
        coreSliceActions.setMessage({
          message: errorMessage,
          success: false,
        }),
      );
      yield put(mfaSliceActions.verifyMfaFailed(errorMessage));
      return;
    }

    const signInData = {
      name: originalCredentials.name,
      password: originalCredentials.password,
      mfaType: payload.mfaType,
      code: payload.code,
      isUsingBackupCode: payload.isUsingBackupCode || false,
      challengeId,
    };

    const response: IRestApiResponse = yield call(request, API.POST_SIGN_IN, signInData, false);
    if (response.statusCode === 201 || response.statusCode === 200) {
      setAuthToken(response.data.idToken.jwtToken);
      setRefreshToken(response.data.refreshToken.token);
      yield put(mfaSliceActions.verifyMfaSucceeded({ idToken: response.data.idToken }));
      yield put(authSliceActions.signInSucceeded({ idToken: response.data.idToken }));
    } else {
      const errorMessage = response?.message
        ? capitalize(response?.message)
        : defaultErrorMessage;
      yield put(
        coreSliceActions.setMessage({
          message: errorMessage,
          success: false,
        }),
      );
      yield put(mfaSliceActions.verifyMfaFailed(errorMessage));
    }
  } catch (error: any) {
    let errorMessage = defaultErrorMessage;
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }
    yield put(
      coreSliceActions.setMessage({
        message: errorMessage,
        success: false,
      }),
    );
    yield put(mfaSliceActions.verifyMfaFailed(errorMessage));
  }
}

export function* mfaSaga() {
  yield all([
    takeLatest(mfaSliceActions.initiateMfaChallenge.type, initiateMfaChallengeGenerator),
    takeLatest(mfaSliceActions.verifyMfa.type, verifyMfaGenerator),
  ]);
}

export default mfaSaga;
